import React, { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import api, { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { ClockCounterClockwise, UsersThree, ArrowsLeftRight, LockKey } from "@phosphor-icons/react";
import { toast } from "sonner";

const ACTION = { assigned: "Auto-assigned", opened_review: "Opened review", authorized: "Authorized", handed_over: "Handed over", rotation_changed: "Changed rotation", sent_back: "Sent back" };

export function AuthLogsButton() {
  const [open, setOpen] = useState(false);
  const { data: logs = [] } = useQuery({ queryKey: ["auth-logs"], enabled: open, queryFn: async () => (await api.get("/carrier-authorization/logs")).data, refetchInterval: open ? 10000 : false });
  return (
    <>
      <Button variant="outline" onClick={() => setOpen(true)} className="border-slate-300 bg-white" data-testid="ca-logs-btn"><ClockCounterClockwise size={16} className="mr-1.5" />Activity log</Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-white border-slate-200 max-w-2xl" data-testid="ca-logs-dialog">
          <DialogHeader><DialogTitle className="font-heading">Authorization activity log</DialogTitle><DialogDescription>Who did what, and when — every action is recorded.</DialogDescription></DialogHeader>
          <div className="max-h-[60vh] overflow-y-auto divide-y divide-slate-100">
            {!logs.length ? <p className="p-4 text-sm text-slate-400">No activity yet.</p> : logs.map((l) => (
              <div key={l.id} className="py-3 flex gap-3" data-testid={`ca-log-${l.id}`}>
                <span className="mt-1 h-2 w-2 shrink-0 rounded-full bg-amber-500" />
                <div className="flex-1 min-w-0"><p className="text-sm text-slate-900"><b>{l.by_name}</b> · {ACTION[l.action] || l.action}{l.company ? <> · <span className="font-semibold">{l.company}</span></> : null}</p>
                  {l.detail && <p className="text-xs text-slate-500">{l.detail}</p>}</div>
                <span className="text-[11px] text-slate-400 whitespace-nowrap">{new Date(l.at).toLocaleString([], { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}</span>
              </div>))}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

export function RotationButton() {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const { data } = useQuery({ queryKey: ["auth-rotation"], queryFn: async () => (await api.get("/carrier-authorization/rotation")).data });
  const [sel, setSel] = useState(null);
  const members = sel || data?.members || [];
  const save = async () => { try { await api.put("/carrier-authorization/rotation", { members }); toast.success("Rotation saved"); setOpen(false); setSel(null); qc.invalidateQueries({ queryKey: ["auth-rotation"] }); } catch (e) { toast.error(apiError(e.response?.data?.detail)); } };
  const name = (id) => data?.admins.find((a) => a.id === id)?.name || "—";
  return (
    <>
      <Button variant="outline" onClick={() => setOpen(true)} className="border-slate-300 bg-white" data-testid="ca-rotation-btn"><UsersThree size={16} className="mr-1.5" />Rotation · next: {data ? name(data.next) : "…"}</Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-white border-slate-200 max-w-md" data-testid="ca-rotation-dialog">
          <DialogHeader><DialogTitle className="font-heading">Authorization rotation</DialogTitle><DialogDescription>Every carrier ready for dispatch goes to the next admin, one by one. All admins can see it; only the assigned admin can work on it.</DialogDescription></DialogHeader>
          <div className="space-y-2">{data?.admins.map((a) => (
            <label key={a.id} className="flex items-center justify-between rounded-xl border border-slate-200 px-3 py-2.5 text-sm" data-testid={`ca-rot-${a.id}`}><span><b className="text-slate-900">{a.name}</b><span className="block text-xs text-slate-500">{a.email}</span></span>
              <input type="checkbox" checked={members.includes(a.id)} onChange={(e) => setSel(e.target.checked ? [...members, a.id] : members.filter((m) => m !== a.id))} className="h-4 w-4" /></label>))}</div>
          <DialogFooter><Button onClick={save} className="bg-slate-900 text-white" data-testid="ca-rotation-save">Save rotation</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

export function LockedBanner({ lead, admins, onDone }) {
  return (
    <div className="flex flex-wrap items-center gap-3 rounded-2xl border border-amber-300 bg-amber-50 px-5 py-4" data-testid="ca-locked">
      <LockKey size={22} weight="fill" className="text-amber-600" />
      <p className="text-sm text-amber-900 flex-1">Assigned to <b>{lead.auth_owner_name || "another admin"}</b>. You can view everything, but only they can complete this authorization.</p>
    </div>
  );
}

export function HandoverButton({ lead, admins, onDone }) {
  const others = admins.filter((a) => a.id !== lead.auth_owner_id);
  if (!others.length) return null;
  const go = async (id) => { try { await api.post(`/carrier-authorization/${lead.id}/handover`, { to: id }); toast.success("Handed over"); onDone(); } catch (e) { toast.error(apiError(e.response?.data?.detail)); } };
  return (
    <select defaultValue="" onChange={(e) => e.target.value && go(e.target.value)} className="h-9 rounded-md border border-white/20 bg-transparent px-2 text-sm text-white/80" data-testid="ca-handover">
      <option value="" className="text-slate-900">Hand over…</option>{others.map((a) => <option key={a.id} value={a.id} className="text-slate-900">{a.name}</option>)}
    </select>
  );
}
